import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Download, ArrowUpRight } from "lucide-react";
import { api, API } from "@/lib/api";
import { redirectToStripe } from "@/lib/redirect";
import { toast } from "sonner";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/s9m8h718_%20-502.jpg";
const PREVIEW_URL = `${API}/audio/preview`;

const PRESETS = [3, 7, 15];

export default function IgLink() {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadToken, setDownloadToken] = useState(null);
  const [audio] = useState(() => (typeof Audio !== "undefined" ? new Audio(PREVIEW_URL) : null));
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    try {
      const t = localStorage.getItem("metamorph_download_token");
      if (t) setDownloadToken(t);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!audio) return;
    const onEnd = () => setPlaying(false);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.pause();
      audio.removeEventListener("ended", onEnd);
    };
  }, [audio]);

  const togglePlay = async () => {
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      try {
        await audio.play();
        setPlaying(true);
      } catch (e) { /* play blocked */ }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (!num || num < 1) {
      toast.error("Choose any amount of $1 or more.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/create-session", {
        amount: num,
        origin_url: window.location.origin,
        kind: "release",
        product: "wav",
      });
      redirectToStripe(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  const downloadUrl = downloadToken ? `${API}/audio/download/${downloadToken}` : null;

  return (
    <main
      className="relative min-h-[100svh] w-full bg-black text-white overflow-hidden flex flex-col"
      data-testid="ig-link-page"
    >
      {/* Backdrop image */}
      <div
        className="absolute inset-0 film-grade"
        style={{
          backgroundImage: `url('${HERO_IMG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.5,
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85 pointer-events-none" aria-hidden />
      <div className="film-vignette" aria-hidden />
      <div className="film-grain" aria-hidden />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-between px-6 py-10 max-w-md mx-auto w-full">
        {/* Header */}
        <div className="text-center mm-fade-in">
          <h1
            className="vintage-text leading-[0.95] tracking-tight text-6xl sm:text-7xl"
            data-testid="ig-title"
          >
            Metamorph
          </h1>
          <p
            className="vintage-text-tight text-[10px] sm:text-[11px] uppercase mt-2 tracking-[0.18em]"
            data-testid="ig-byline"
          >
            by George Maple
          </p>

          {/* Mini play */}
          <button
            onClick={togglePlay}
            className="mt-6 inline-flex items-center gap-2 vintage-link text-[11px] uppercase opacity-90 hover:opacity-100 transition-opacity"
            data-testid="ig-play-toggle"
            aria-label={playing ? "Pause preview" : "Play preview"}
          >
            <span className="w-7 h-7 rounded-full border border-white/40 flex items-center justify-center">
              {playing ? (
                <Pause className="w-3 h-3" strokeWidth={1.2} />
              ) : (
                <Play className="w-3 h-3 translate-x-[1px]" strokeWidth={1.2} />
              )}
            </span>
            {playing ? "Pause preview" : "Play preview"}
          </button>
        </div>

        {/* Action */}
        {downloadUrl ? (
          <div className="w-full text-center mm-fade-in d2">
            <p className="vintage-link text-[11px] uppercase tracking-[0.22em] mb-6 opacity-85">
              Your link is ready
            </p>
            <a
              href={downloadUrl}
              download
              className="btn-line btn-filled w-full justify-center"
              data-testid="ig-download"
            >
              Download WAV <Download className="w-4 h-4" strokeWidth={1} />
            </a>
            <Link
              to="/purchase"
              className="vintage-link text-[10px] uppercase mt-6 inline-block opacity-70"
              data-testid="ig-buy-another"
            >
              Buy another · or with artwork
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full mm-fade-in d2" data-testid="ig-form">
            <p className="vintage-link text-[11px] uppercase tracking-[0.22em] text-center mb-4 opacity-85">
              Pay what you can
            </p>
            <div className="flex items-end justify-center gap-2 mb-5">
              <span className="vintage-text text-3xl opacity-60 leading-none">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="1"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-transparent border-none outline-none vintage-text text-5xl sm:text-6xl text-center w-40"
                placeholder="0"
                data-testid="ig-amount"
                aria-label="Amount in USD"
              />
              <span className="font-mono text-[10px] text-white/45 tracking-[0.3em] pb-2">USD</span>
            </div>

            <div className="flex justify-center gap-2 mb-6">
              {PRESETS.map((p) => (
                <button
                  type="button"
                  key={p}
                  onClick={() => setAmount(String(p))}
                  className={`px-3 py-1.5 border text-[10px] tracking-[0.18em] uppercase transition-all duration-500 ${
                    parseFloat(amount) === p
                      ? "border-white text-white"
                      : "border-white/25 text-white/65 hover:border-white/60 hover:text-white"
                  }`}
                  data-testid={`ig-preset-${p}`}
                >
                  ${p}
                </button>
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-line btn-filled w-full justify-center"
              data-testid="ig-submit"
            >
              {loading ? "Opening checkout…" : "Pay & download"}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1} />
            </button>

            <p className="text-center text-[9px] tracking-[0.25em] uppercase text-white/45 mt-3" data-testid="ig-pay-helper">
              Apple Pay · Google Pay · Card
            </p>
          </form>
        )}

        {/* Footer */}
        <div className="w-full flex flex-col items-center gap-4 mm-fade-in d3">
          <p
            className="vintage-link uppercase flex items-baseline justify-center gap-2"
            data-testid="ig-proceeds"
          >
            <span className="vintage-text text-2xl">100%</span>
            <span className="text-[9px] tracking-[0.22em]">to the artist</span>
          </p>
          <div className="flex items-center justify-center gap-5">
            <Link to="/tickets" className="vintage-link text-[10px] uppercase opacity-80" data-testid="ig-tickets">
              Tickets
            </Link>
            <span className="vintage-link text-[10px] opacity-40">·</span>
            <Link to="/manifesto" className="vintage-link text-[10px] uppercase opacity-80" data-testid="ig-manifesto">
              Manifesto
            </Link>
            <span className="vintage-link text-[10px] opacity-40">·</span>
            <Link to="/purchase" className="vintage-link text-[10px] uppercase opacity-80" data-testid="ig-full-site">
              Full site
            </Link>
            <span className="vintage-link text-[10px] opacity-40">·</span>
            <Link to="/contact" className="vintage-link text-[10px] uppercase opacity-80" data-testid="ig-contact">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
